import type { RoundConfiguration } from "Models";
import type { Clubhouse_RoundSetting } from "../../types/leagues";
import type { SeasonRoundItem } from "../leagueFormSchema";
import { settingsForRound } from "./roundConditions";
import type {
  AutoConcede,
  AutoContinue,
  AutoGimme,
  GimmeDistance,
  GroundConditions,
  PinType,
  SkyConditions,
  TimeOfDay,
  WindDirection,
  WindSpeed,
} from "../../enums";

/**
 * Seed a league's generated rounds from a saved Round Preset.
 *
 * Replaces seeding from the facility's default round settings. A preset is the
 * operator's own named configuration — they already build these for ad-hoc
 * rounds — so a league can be set up by picking the one it should run on,
 * rather than by editing every generated round to say the same thing.
 *
 * Takes only the **conditions and automation**. A preset also stores a course,
 * a hole selection, tees and handicap settings, and none of those belong on a
 * league round:
 *   - **course** — rounds are assigned courses round-robin across the facility's
 *     list, which is a feature of the season, not something a preset should
 *     flatten to one course.
 *   - **holes** — chosen per round.
 *   - **tees** — the league owns these now; Season Setup has its own men's and
 *     women's tee rows, and round settings deliberately stopped carrying tees.
 *   - **handicap and scoring** — the league's own scoring and handicap policy
 *     governs how rounds are played; a preset must not override it.
 *
 * `roundInfo` uses the same `types/enums` family the facility defaults did,
 * whose numeric values match the leagues-v2 enums member for member, so this
 * maps by value and never by name.
 */
export function roundPresetToRoundSettings(
  preset: RoundConfiguration | undefined,
): Clubhouse_RoundSetting | undefined {
  const round = preset?.roundInfo;
  if (!round) return undefined;

  const mapped: Clubhouse_RoundSetting = {
    // Conditions
    pinType: round.pinType as unknown as PinType,
    stimpSetting: round.stimpSetting,
    windSpeed: round.windSpeed as unknown as WindSpeed,
    windDirection: round.windDirection as unknown as WindDirection,
    skyConditions: round.skyConditions as unknown as SkyConditions,
    groundConditions: round.groundConditions as unknown as GroundConditions,
    timeConditions: round.timeConditions as unknown as TimeOfDay,
    // Automation
    autoConcede: round.autoConcede as unknown as AutoConcede,
    // Top-level on the preset, not inside roundInfo: the create form writes it
    // unprefixed (`setValue("autoContinue", …)`), and `Round.autoContinue` is
    // an optional field it never fills. Reading roundInfo alone silently gave
    // every league round `undefined`.
    autoContinue: (preset.autoContinue ??
      round.autoContinue) as unknown as AutoContinue,
    autoDrop: round.autoDrop,
    autoGimmes: round.autoGimmes as unknown as AutoGimme,
    gimmeDistance: round.gimmeDistance as unknown as GimmeDistance,
    mulligans: round.mulligans,
  };

  // An older preset can hold nothing but the fields a league ignores — a
  // course, a tee, a handicap mode. Mapping it would then yield an object of
  // nothing but undefined keys, which reads as "settings present" to anything
  // counting keys but serializes to `{}` on the wire: the absent-or-cleared
  // ambiguity CLUB-2414 and CLUB-2407 both turned on. Drop the absent keys and
  // say "no settings" outright when none survive.
  //
  // Only `undefined` is dropped: 0 and false are real settings (pin NOVICE,
  // wind CALM, mulligans off).
  const present = Object.fromEntries(
    Object.entries(mapped).filter(([, value]) => value !== undefined),
  ) as Clubhouse_RoundSetting;

  return Object.keys(present).length > 0 ? present : undefined;
}

/**
 * Re-seed every generated round from a newly chosen settings source.
 *
 * `mergePlanWithEdits` deliberately prefers the settings already stored on a
 * round over the plan's, which is what keeps an operator's per-round edits
 * alive across a tab switch. The cost is that a regenerated plan can't change
 * settings on its own — so a change of source has to rewrite them outright.
 *
 * Holes are preserved: they're a per-round choice, not part of the source.
 */
export function withSettingsSource(
  rounds: SeasonRoundItem[],
  settings: Clubhouse_RoundSetting | undefined,
  varyConditions = false,
): SeasonRoundItem[] {
  return rounds.map((round) => ({
    ...round,
    settings: {
      ...settingsForRound(round.roundNumber, settings, varyConditions),
      ...(round.settings?.selectedHoles
        ? { selectedHoles: round.settings.selectedHoles }
        : {}),
    },
  }));
}
